import { useCallback, useContext, useState, createContext } from 'react';
import type { ReactNode } from 'react';
import { Tag, message } from 'antd';
import dayjs from 'lib/dayjs';
import {
  useMakeConversationCache,
  useGetConversationInfo,
  useCurrentConversationIdValue,
  useSetConversationIdListValue,
  useSetCurrentConversationIdValue,
} from 'hooks/business/conversation';
import { useSearchMsgStoreName } from 'hooks/business/searchMsgStoreName';
import { useSearchMsgWord } from 'hooks/business/searchMsgWord';
import { useSetSearchMsgSelectedSendTime } from 'hooks/business/searchMsgSelectedMsg';
import { createConversation, getTalkInfoByBuyerStore } from 'services/api/refund';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { useConversationOrder } from 'hooks/business/conversationsHooks/useOpenConversation';
import style from './SearchStyle';
import { uniq } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import posthog from '@leyan/lytics';
import { useMemoizedFn } from 'ahooks';
import { updateConversationV2 } from 'services/api/conversation.convList';
import { useUpdateConversationOfId } from 'hooks/business/conversationsHooks/useConversationOfId';
import { useAuthorization } from 'components/Authorization';
import { isIdEqual } from 'utils/is';
import { isValidConversationId } from '../Chat/utils';

export interface IOrderNoconv {
  region: General.UpperCaseRegion;
  channel: General.PlatformUpperCase;
  externalStoreId: string;
  externalBuyerId: string;
  buyerNick: string;
  orderId: string;
}

export function useFetchCreateConversation() {
  const setConversation = useMakeConversationCache('set');
  const { t } = useTranslate();

  return useCallback(
    async (params: IOrderNoconv, type: string) => {
      try {
        // whatapp 需要手机号，调用接口
        const { talkInfos } = await getTalkInfoByBuyerStore({
          items: [params],
          needPhone: true,
        });
        const convInfo = await createConversation({
          ...params,
          ...(type === 'order'
            ? {
                sourceAction: 'SEARCH_ORDER',
              }
            : {}),
        });
        posthog.capture('$useFetchCreateConversation', {
          params,
          talkInfos,
          convInfo,
          sourceAction: type,
        });
        setConversation({
          ...(talkInfos?.[0] || {}),
          ...convInfo,
        });
        return convInfo;
      } catch (e) {
        message.error(t('common.feedback.fetchFailed'));
        throw e;
      }
    },
    [setConversation, t],
  );
}

type SearchPageItemListContextValue = {
  selMsgId: string;
  setSelMsgId: React.Dispatch<React.SetStateAction<string>>;
  itemClick: (
    convId: string,
    sendTime: string,
    msgId: string,
    r: Conversation.SearchItemUI,
    searchMsgWord: string,
    type: string,
  ) => Promise<void>;
  storeIdNameMap: ReturnType<typeof useSearchMsgStoreName>;
  searchMsgWord: string;
  t: ReturnType<typeof useTranslate>['t'];
};

const SearchPageItemListContext = createContext<SearchPageItemListContextValue | null>(null);

/**
 * 包裹同一列表内的多条 SearchPageItem。
 * 在子树内只创建一份点击防抖、店铺名映射与选中高亮状态（与原 useShowItem 一致）。
 */
export function SearchPageItemSelectionProvider({
  children,
  prefetchConversationsMap,
}: {
  children: ReactNode;
  prefetchConversationsMap: Record<string, Conversation.Conversation>;
}) {
  const [selMsgId, setSelMsgId] = useState('');
  const [isOpeningConversation, setIsOpeningConversation] = useState(false);
  const storeIdNameMap = useSearchMsgStoreName();
  const searchMsgWord = useSearchMsgWord();
  const { t } = useTranslate();
  const setConversationIdList = useSetConversationIdListValue();
  const getConversationInfo = useGetConversationInfo();
  const setSearchMsgSelectedSendTime = useSetSearchMsgSelectedSendTime();
  const { onlineSearch } = useFlagConfigValue();
  const currentConversationId = useCurrentConversationIdValue();
  const conversationOrder = useConversationOrder();
  const updateConversationOfId = useUpdateConversationOfId();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const setCurrentConversationIdValue = useSetCurrentConversationIdValue();
  const fetchCreateConversation = useFetchCreateConversation();

  const openConversationFromSearchPage = useMemoizedFn(
    async (conversation: Conversation.Conversation, orderSn?: string, sourceCode?: string) => {
      const { conversationId, supervisePower } = conversation;
      setCurrentConversationIdValue(conversationId);

      try {
        conversationOrder(conversation, orderSn);

        if (!supervisePower) {
          const newConversation = await updateConversationV2({
            conversationId: conversationId,
            clearMark: true,
            acquireControl: true,
            ...(sourceCode ? { sourceCode } : {}),
          });
          const { receptionId, receptionName } = newConversation;
          const controlStatus = isIdEqual(payload?.id, receptionId);
          updateConversationOfId(
            {
              controlStatus,
              receptionName: controlStatus ? '' : receptionName,
              receptionId,
              transferMark: false,
              superviseMark: false,
              botMark: false,
              localError: undefined,
            },
            conversationId,
          );
        }
      } catch (err) {
        updateConversationOfId(
          {
            localError: supervisePower ? 'API_ERROR' : 'API_ACQUIRE_CONTROL_ERROR',
          },
          conversationId,
        );
      }
    },
  );

  const itemClick = useMemoizedFn(
    async (
      convId: string,
      sendTime: string,
      msgId: string,
      r: Conversation.SearchItemUI,
      searchMsgWord: string,
      type: string,
    ) => {
      if (isOpeningConversation || convId === currentConversationId) {
        return;
      }

      setIsOpeningConversation(true);

      try {
        let convInfo: Conversation.Conversation;

        const orderSn = r.tradeId || searchMsgWord.trim();
        let conversationId = convId;

        const prefetchConvInfo = prefetchConversationsMap[convId];
        if (prefetchConvInfo && isValidConversationId(prefetchConvInfo.conversationId)) {
          convInfo = await getConversationInfo(convId);
        } else if (
          onlineSearch &&
          r.region &&
          r.platform &&
          r.externalBuyerId &&
          r.externalStoreId
        ) {
          // 后端搜索 创建静默单
          convInfo = await fetchCreateConversation(
            {
              region: r.region,
              channel: r.platform,
              externalStoreId: r.externalStoreId,
              externalBuyerId: r.externalBuyerId,
              buyerNick: r.buyerName,
              orderId: orderSn,
            },
            type,
          );
          conversationId = convInfo.conversationId;
        } else {
          message.error(t('common.feedback.fetchFailed'));
          return;
        }

        if (msgId && sendTime && msgId !== '0' && sendTime !== '0') {
          setSearchMsgSelectedSendTime({
            sendTime,
            msgId,
          });
        } else {
          setSearchMsgSelectedSendTime({
            sendTime: '',
            msgId: '',
          });
        }
        openConversationFromSearchPage(
          convInfo,
          orderSn,
          type === 'order' ? 'SEARCH_ORDER' : undefined,
        );
        setConversationIdList((pre) => uniq([...pre, conversationId]));
      } finally {
        setIsOpeningConversation(false);
      }
    },
  );

  return (
    <SearchPageItemListContext.Provider
      value={{ selMsgId, setSelMsgId, itemClick, storeIdNameMap, searchMsgWord, t }}
    >
      {children}
    </SearchPageItemListContext.Provider>
  );
}

export const searchOrderRef = { current: false };

export function SearchPageItem(props: {
  type: Conversation.LocalSearchType;
  r: Conversation.SearchItemUI;
}) {
  const { type, r } = props;
  const listCtx = useContext(SearchPageItemListContext);

  if (!listCtx) {
    console.warn('SearchPageItem must be used within SearchPageItemSelectionProvider');
    return null;
  }

  const { selMsgId, setSelMsgId, itemClick, storeIdNameMap, searchMsgWord, t } = listCtx;

  let buyerName;
  if (type === 'buyer' && r.buyerName) {
    const bn = r.buyerName;
    const s = bn.toLowerCase().indexOf(searchMsgWord.toLowerCase());
    buyerName = (
      <>
        <span>{bn.substr(0, s)}</span>
        <span className={style.wordHighLight}>{bn.substr(s, searchMsgWord.length)}</span>
        <span>{bn.substr(s + searchMsgWord.length)}</span>
      </>
    );
  } else {
    buyerName = r.buyerName;
  }
  let { cnt }: { cnt: string | JSX.Element } = r;
  if ((type === 'text' || type === 'product') && cnt) {
    const bn = r.cnt;
    const s = bn.toLowerCase().indexOf(searchMsgWord.toLowerCase());
    const maxLenShow = 20;
    let from = 0;
    const showEndPos = s + searchMsgWord.length;
    if (showEndPos > maxLenShow) {
      from = showEndPos - maxLenShow + maxLenShow / 2;
      if (from > s) {
        from = s - maxLenShow / 2;
      }
    }
    cnt = (
      <>
        <span>{bn.substring(from, s)}</span>
        {s > -1 ? (
          <>
            <span className={style.wordHighLight}>{bn.substr(s, searchMsgWord.length)}</span>
            <span>{bn.substr(s + searchMsgWord.length)}</span>
          </>
        ) : (
          <span>{bn}</span>
        )}
      </>
    );
  }

  let msgTime = '';
  let msgId = '';
  if (r.msgId) {
    msgTime = r.msgTime;
    msgId = r.msgId;
  }
  let selMsgClass = style.searchItem;
  if (r.msgId && selMsgId === r.msgId + r.tradeId) {
    selMsgClass = `${selMsgClass} on`;
  }

  return (
    <div
      className={selMsgClass}
      onClick={() => {
        itemClick(r.convId, msgTime, msgId, r, searchMsgWord, type);
        searchOrderRef.current = type === 'order';
        if (msgId) {
          setSelMsgId(msgId + r.tradeId);
        }
      }}
    >
      <div className={style.titleWrap}>
        <div>
          <span className={`${style.buyerNick} ${style.txtHide}`} title={r.buyerName}>
            {buyerName}
          </span>
          {r.isSellerAffiliateChat && (
            <Tag className={style.daren}>
              {t('scene.chatWorkspace.conversationList.search.label.influencer')}
            </Tag>
          )}
        </div>
        <span>
          {r.msgTime && r.msgTime !== '0'
            ? dayjs(parseInt(r.msgTime, 10)).format('MM-DD HH:mm')
            : null}
        </span>
      </div>
      <div className={style.contentWrap}>
        {type === 'product' ? (
          <span className={style.typeTip}>[{t(`tab.${type}`)}]</span>
        ) : type === 'order' && r.tradeId ? (
          <span className={`${style.typeTip} ${style.wordHighLight}`}>[{r.tradeId}]</span>
        ) : (
          <span className={`${style.content} ${style.txtHide}`}>{cnt}</span>
        )}
      </div>
      <Tag
        className={`${style.storeName} ${style.txtHide}`}
        title={r.storeAlias || r.storeName || storeIdNameMap[r.shopId]}
      >
        {r.storeAlias || r.storeName || storeIdNameMap[r.shopId] || ''}
      </Tag>
    </div>
  );
}

export default SearchPageItem;
